import React, { useMemo } from 'react';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Text,
  useColorModeValue,
  Box,
  HStack,
} from '@chakra-ui/react';
import { useTable, useSortBy } from 'react-table';
import { prettifyDate } from '../../../../functions/dateUtils';

const ReferralSearchTable = ({ data, onViewDetails }) => {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'fullName',
        width: '200px',
      },
      {
        Header: 'Company',
        accessor: 'companyName',
        width: '200px',
      },
      {
        Header: 'Referral Code',
        accessor: 'referralCode',
        Cell: ({ value }) => (
          <Badge colorScheme="purple" variant="subtle">
            {value}
          </Badge>
        ),
        width: '150px',
      },
      {
        Header: 'Referral Summary',
        accessor: 'referralSummary',
        Cell: ({ row }) => {
          const { totalReferrals, withApplications } = row.original;
          return (
            <HStack spacing={2}>
              <Badge colorScheme="blue" size="sm">
                Total: {totalReferrals}
              </Badge>
              {withApplications > 0 && (
                <Badge colorScheme="green" size="sm">
                  Active: {withApplications}
                </Badge>
              )}
            </HStack>
          );
        },
        width: '250px',
        disableSortBy: true,
      },
      {
        Header: 'Conversion Rate',
        accessor: 'applicationConversionRate',
        Cell: ({ value }) => (
          <Text fontWeight="semibold" color={value >= 70 ? 'green.500' : value >= 50 ? 'yellow.500' : 'red.500'}>
            {value}%
          </Text>
        ),
        width: '120px',
      },
      {
        Header: 'Registration Date',
        accessor: 'registrationDate',
        Cell: ({ value }) => prettifyDate(value),
        width: '150px',
      },
      {
        Header: 'Actions',
        accessor: 'actions',
        Cell: ({ row }) => (
          <Button
            size="sm"
            colorScheme="blue"
            variant="outline"
            onClick={() => onViewDetails(row.original)}
          >
            View Details
          </Button>
        ),
        width: '120px',
        disableSortBy: true,
      },
    ],
    [onViewDetails]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: [
          {
            id: 'applicationConversionRate',
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  if (!data || data.length === 0) {
    return (
      <Box textAlign="center" py={8}>
        <Text color="gray.500">No search results</Text>
      </Box>
    );
  }

  return (
    <Box overflowX="auto" bg="white" borderRadius="lg" boxShadow="sm">
      <Table {...getTableProps()} variant="simple" size="md">
        <Thead>
          {headerGroups.map((headerGroup, index) => (
            <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
              {headerGroup.headers.map((column, columnIndex) => (
                <Th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  key={columnIndex}
                  color="gray.400"
                  fontSize="sm"
                  fontWeight="700"
                  textTransform="uppercase"
                  borderColor={borderColor}
                  cursor={column.canSort ? 'pointer' : 'default'}
                  _hover={column.canSort ? { color: 'blue.400' } : {}}
                >
                  {column.render('Header')}
                  {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody {...getTableBodyProps()}>
          {rows.map((row, index) => {
            prepareRow(row);
            return (
              <Tr {...row.getRowProps()} key={index}>
                {row.cells.map((cell, cellIndex) => (
                  <Td
                    {...cell.getCellProps()}
                    key={cellIndex}
                    fontSize="sm"
                    color={textColor}
                    borderColor={borderColor}
                  >
                    {cell.render('Cell')}
                  </Td>
                ))}
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};

export default ReferralSearchTable;
